import type { StyleRecipe, StyleRecipeId, ProfileData } from "./types";

/**
 * 6 Canonical Flagship Style Recipes
 * Each recipe defines a complete structural and art-directed design contract.
 */
export const STYLE_RECIPES: Record<StyleRecipeId, StyleRecipe> = {
  "obsidian-executive": {
    id: "obsidian-executive",
    name: "Obsidian Executive",
    description: "Authoritative dark graphite canvas with brushed champagne accents, subtle depth, and precision laser tracking.",
    isPro: true,
    baseTheme: "modern-executive",
    defaultPalette: "obsidian-executive",
    defaultFontPairing: "modern-sans",
    layout: {
      model: "linear",
      maxWidth: "min(920px, 94vw)",
      contentMeasure: "68ch",
      sectionGap: "clamp(3.5rem, 6vw, 5.5rem)",
      gridGap: "1.5rem",
    },
    typography: {
      headingFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
      bodyFamily: "'Inter', -apple-system, sans-serif",
      metadataFamily: "'JetBrains Mono', monospace",
      scale: "editorial",
      headingStyle: "sans",
    },
    geometry: {
      radius: "8px",
      borderStyle: "1px solid var(--site-border-subtle, rgba(255, 255, 255, 0.07))",
      shadowStyle: "0 8px 32px -4px rgba(0, 0, 0, 0.6)",
      cardPadding: "clamp(1.25rem, 3vw, 2rem)",
      radiusRoles: {
        sectionRadius: "0px",
        cardRadius: "8px",
        controlRadius: "6px",
        badgeRadius: "9999px",
        avatarRadius: "16px",
        dialogRadius: "12px",
      },
    },
    heroDefaults: {
      avatarShape: "squircle",
      avatarSize: "lg",
      avatarBorder: "thin",
      avatarBorderColor: "#e5b964",
      avatarShadow: "elevated",
      nameTracking: "tight",
      heroLayout: "split-left",
      nameAlign: "left",
      nameWeight: "extrabold",
    },
    components: {
      hero: "executive-split-seamless",
      navigation: "frosted-pill",
      sectionHeader: "champagne-insignia",
      filterBar: "graphite-pill",
      highlights: "executive-stat-ribbon",
      publication: "flat-citation-list",
      research: "topic-cards",
      experience: "grid-timeline",
      teaching: "compact-course-cards",
      project: "media-frame-preview",
      resource: "flat-resource-row",
      skills: "grouped-domain-list",
      contact: "balanced-channel-grid",
      dialog: "elevated-slate-modal",
    },
    effects: {
      level: "subtle",
      glass: true,
      glow: false,
      hoverLift: true,
      ambientBackground: false,
    },
  },

  "sapphire-scholar": {
    id: "sapphire-scholar",
    name: "Sapphire Scholar",
    description: "Classical academic dignity on alabaster paper, hanging indent citations, and formal Oxford navy typography.",
    isPro: false,
    baseTheme: "classic-academic",
    defaultPalette: "oxford-navy",
    defaultFontPairing: "serif-classic",
    layout: {
      model: "linear",
      maxWidth: "min(860px, 94vw)",
      contentMeasure: "65ch",
      sectionGap: "clamp(2rem, 4.5vw, 3.5rem)",
      gridGap: "1.25rem",
    },
    typography: {
      headingFamily: "'Cinzel', 'Playfair Display', serif",
      bodyFamily: "'Source Serif 4', 'Lora', serif",
      metadataFamily: "'JetBrains Mono', monospace",
      scale: "editorial",
      headingStyle: "serif",
    },
    geometry: {
      radius: "0.25rem",
      borderStyle: "1px solid var(--site-border-subtle, #e2e8f0)",
      shadowStyle: "0 2px 10px rgba(15, 23, 42, 0.04)",
      cardPadding: "clamp(1.25rem, 3vw, 2.25rem)",
    },
    heroDefaults: {
      avatarShape: "circle",
      avatarSize: "lg",
      avatarBorder: "double",
      avatarBorderColor: "#1e3a8a",
      avatarShadow: "subtle",
      nameTracking: "normal",
      heroLayout: "standard",
      nameAlign: "left",
      nameWeight: "bold",
    },
    components: {
      hero: "classical-seal",
      navigation: "dignified-bar",
      sectionHeader: "classical-rule",
      filterBar: "small-caps-tab",
      highlights: "academic-credential-block",
      publication: "academic-biblio",
      research: "academic-treatise",
      experience: "academic-cv",
      teaching: "classical-dossier",
      project: "academic-grant",
      resource: "ivory-sheet",
      skills: "academic-hierarchy",
      contact: "directory-card",
      dialog: "ivory-paper",
    },
    effects: {
      level: "none",
      glass: false,
      glow: false,
      hoverLift: false,
      ambientBackground: false,
    },
  },

  "porcelain-swiss": {
    id: "porcelain-swiss",
    name: "Porcelain Swiss",
    description: "International Typographic Style: pure flat monochrome, 0px sharp geometry, numbered sections, and strict architectural whitespace.",
    isPro: false,
    baseTheme: "minimalist-scholar",
    defaultPalette: "minimal-monochrome",
    defaultFontPairing: "modern-sans",
    layout: {
      model: "linear",
      maxWidth: "min(800px, 94vw)",
      contentMeasure: "62ch",
      sectionGap: "clamp(2.5rem, 6vw, 4.5rem)",
      gridGap: "1.5rem",
    },
    typography: {
      headingFamily: "'Outfit', 'Plus Jakarta Sans', sans-serif",
      bodyFamily: "'Inter', -apple-system, sans-serif",
      metadataFamily: "'JetBrains Mono', monospace",
      scale: "compact",
      headingStyle: "sans",
    },
    geometry: {
      radius: "0px",
      borderStyle: "1px solid #000000",
      shadowStyle: "none",
      cardPadding: "clamp(1rem, 2.5vw, 1.75rem)",
    },
    heroDefaults: {
      avatarShape: "square",
      avatarSize: "md",
      avatarBorder: "thin",
      avatarBorderColor: "#000000",
      avatarShadow: "none",
      nameTracking: "tight",
      heroLayout: "minimal",
      nameAlign: "left",
      nameWeight: "black",
      nameStyle: "uppercase",
    },
    components: {
      hero: "swiss-typographic",
      navigation: "stark-hairline",
      sectionHeader: "swiss-numbered",
      filterBar: "stark-outline-tab",
      highlights: "swiss-stat-box",
      publication: "swiss-table",
      research: "swiss-matrix",
      experience: "swiss-chronology",
      teaching: "swiss-list",
      project: "swiss-plate",
      resource: "swiss-data-row",
      skills: "swiss-monospace",
      contact: "swiss-directory",
      dialog: "swiss-box",
    },
    effects: {
      level: "none",
      glass: false,
      glow: false,
      hoverLift: false,
      ambientBackground: false,
    },
  },

  "cyber-research": {
    id: "cyber-research",
    name: "Cyber Research",
    description: "High-density 12-column bento HUD, glowing cyan terminal prompts, telemetry metrics, and contrast-safe dark cards.",
    isPro: true,
    baseTheme: "modern-bento",
    defaultPalette: "cyber-teal",
    defaultFontPairing: "technical",
    layout: {
      model: "bento",
      maxWidth: "min(1140px, 96vw)",
      contentMeasure: "64ch",
      sectionGap: "clamp(1.5rem, 3.5vw, 2.5rem)",
      gridGap: "clamp(0.75rem, 1.5vw, 1.25rem)",
    },
    typography: {
      headingFamily: "'Space Grotesk', sans-serif",
      bodyFamily: "'IBM Plex Sans', 'Inter', sans-serif",
      metadataFamily: "'JetBrains Mono', monospace",
      scale: "compact",
      headingStyle: "mono",
    },
    geometry: {
      radius: "0.625rem",
      borderStyle: "1px solid rgba(0, 240, 255, 0.2)",
      shadowStyle: "0 4px 20px -2px rgba(0, 0, 0, 0.5), 0 0 15px -3px rgba(0, 240, 255, 0.12)",
      cardPadding: "clamp(1rem, 2vw, 1.5rem)",
    },
    heroDefaults: {
      avatarShape: "squircle",
      avatarSize: "lg",
      avatarBorder: "thin",
      avatarBorderColor: "#00f0ff",
      avatarShadow: "glow",
      nameTracking: "wide",
      heroLayout: "card",
      nameAlign: "left",
      nameWeight: "bold",
    },
    components: {
      hero: "bento-hud",
      navigation: "terminal-dock",
      sectionHeader: "terminal-prompt",
      filterBar: "terminal-bracket-tab",
      highlights: "telemetry-stat-block",
      publication: "tech-module",
      research: "bento-tiles",
      experience: "circuit-trace",
      teaching: "bento-courses",
      project: "bento-telemetry",
      resource: "terminal-payload",
      skills: "telemetry-bars",
      contact: "terminal-comms",
      dialog: "terminal-hud",
    },
    effects: {
      level: "expressive",
      glass: true,
      glow: true,
      hoverLift: true,
      ambientBackground: true,
    },
  },

  "editorial-atelier": {
    id: "editorial-atelier",
    name: "Editorial Atelier",
    description: "Warm natural linen monograph with terracotta accents, deep espresso text, literary drop caps, and magazine pacing.",
    isPro: true,
    baseTheme: "classic-academic",
    defaultPalette: "oxford-navy",
    defaultFontPairing: "editorial",
    layout: {
      model: "editorial",
      maxWidth: "min(820px, 94vw)",
      contentMeasure: "66ch",
      sectionGap: "clamp(2.5rem, 5vw, 4rem)",
      gridGap: "1.5rem",
    },
    typography: {
      headingFamily: "'Playfair Display', 'Cormorant Garamond', serif",
      bodyFamily: "'Source Serif 4', 'Lora', serif",
      metadataFamily: "'DM Mono', 'JetBrains Mono', monospace",
      scale: "editorial",
      headingStyle: "serif",
    },
    geometry: {
      radius: "1rem",
      borderStyle: "1px solid var(--site-border-subtle, #e8e3d9)",
      shadowStyle: "0 4px 20px rgba(44, 36, 32, 0.04)",
      cardPadding: "clamp(1.5rem, 3.5vw, 2.5rem)",
    },
    heroDefaults: {
      avatarShape: "rounded",
      avatarSize: "xl",
      avatarBorder: "thin",
      avatarBorderColor: "#c25e38",
      avatarShadow: "elevated",
      nameTracking: "normal",
      nameStyle: "italic",
      heroLayout: "split-left",
      nameAlign: "left",
      nameWeight: "bold",
    },
    components: {
      hero: "editorial-monograph",
      navigation: "warm-linen",
      sectionHeader: "editorial-serif",
      filterBar: "warm-linen-pill",
      highlights: "terracotta-callout-card",
      publication: "editorial-feature",
      research: "editorial-essay",
      experience: "humanist-journey",
      teaching: "editorial-reading",
      project: "editorial-plaque",
      resource: "linen-strip",
      skills: "humanist-cluster",
      contact: "atelier-envelope",
      dialog: "warm-cream",
    },
    effects: {
      level: "subtle",
      glass: false,
      glow: false,
      hoverLift: true,
      ambientBackground: false,
    },
  },

  "aurora-showcase": {
    id: "aurora-showcase",
    name: "Aurora Showcase",
    description: "Fluid iridescent portfolio with multi-hue gradient accents, specular frosted glass, and 20px pill geometry.",
    isPro: true,
    baseTheme: "grid-showcase",
    defaultPalette: "aurora",
    defaultFontPairing: "modern-sans",
    layout: {
      model: "showcase",
      maxWidth: "min(1080px, 96vw)",
      contentMeasure: "68ch",
      sectionGap: "clamp(2.5rem, 5vw, 4rem)",
      gridGap: "clamp(1rem, 2.5vw, 2rem)",
    },
    typography: {
      headingFamily: "'Syne', 'Plus Jakarta Sans', sans-serif",
      bodyFamily: "'Inter', -apple-system, sans-serif",
      metadataFamily: "'JetBrains Mono', monospace",
      scale: "display",
      headingStyle: "sans",
    },
    geometry: {
      radius: "1.25rem",
      borderStyle: "1px solid rgba(139, 92, 246, 0.3)",
      shadowStyle: "0 8px 32px rgba(139, 92, 246, 0.15), 0 0 20px rgba(6, 182, 212, 0.1)",
      cardPadding: "clamp(1.5rem, 3.5vw, 2.5rem)",
    },
    heroDefaults: {
      avatarShape: "circle",
      avatarSize: "xl",
      avatarBorder: "gradient",
      avatarBorderGradient: "aurora",
      avatarShadow: "glow",
      nameTracking: "tight",
      nameStyle: "gradient",
      heroLayout: "standard",
      nameAlign: "center",
      nameWeight: "extrabold",
    },
    components: {
      hero: "aurora-showcase",
      navigation: "frosted-glass",
      sectionHeader: "aurora-gradient",
      filterBar: "frosted-gradient-tab",
      highlights: "specular-stat-capsule",
      publication: "showcase-card",
      research: "showcase-media",
      experience: "aurora-track",
      teaching: "showcase-modules",
      project: "showcase-gallery",
      resource: "frosted-pill",
      skills: "aurora-capsules",
      contact: "frosted-grid",
      dialog: "specular-glass",
    },
    effects: {
      level: "expressive",
      glass: true,
      glow: true,
      hoverLift: true,
      ambientBackground: true,
    },
  },
};

export const STYLE_RECIPE_LIST: StyleRecipe[] = Object.values(STYLE_RECIPES);

export function getStyleRecipe(id: string | null | undefined): StyleRecipe | undefined {
  if (!id) return undefined;
  return STYLE_RECIPES[id as StyleRecipeId];
}

/**
 * Resolves the canonical recipe for a profile.
 * If the profile has an explicit styleRecipe, use it.
 * Otherwise, cleanly fallback to the legacy themeKey mapping.
 */
export function resolveRecipeForProfile(profile: ProfileData): StyleRecipe {
  if (profile.styleRecipe && STYLE_RECIPES[profile.styleRecipe as StyleRecipeId]) {
    return STYLE_RECIPES[profile.styleRecipe as StyleRecipeId];
  }

  const legacyMap: Record<string, StyleRecipeId> = {
    "modern-executive": "obsidian-executive",
    "classic-academic": "sapphire-scholar",
    "standard": "sapphire-scholar",
    "minimalist-scholar": "porcelain-swiss",
    "modern-bento": "cyber-research",
    "grid-showcase": "aurora-showcase",
  };

  const recipeId = legacyMap[profile.themeKey || ""] || "sapphire-scholar";
  return STYLE_RECIPES[recipeId];
}
